import React from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { getChargeMapping, TOP_10_MASTER_FILTER } from "../../utils/key_helper";
import { CHILD_ACCOUNT_STATUS, TRANSACTION_TYPE, ORDER_EXECUTION_TYPE, STRATEGY_CHARGES_MODE } from "../../utils/key_helper";
export const WithTooltip = ({ tooltipText, placement = 'top', children }) => {
    const tooltipId = React.useId();
    return (
        <>
            <span data-tooltip-id={tooltipId} data-tooltip-content={tooltipText}>
                {children}
            </span>
            <ReactTooltip id={tooltipId} event="click" place={placement} className="custom-tooltip" />
        </>
    );
};

export const commonSwitchProps = {
    offColor: "#808080",
    onColor: "#7ea2ff",
    checkedIcon: false,
    uncheckedIcon: false,
    height: 18,
    width: 36
};
export const smallSwitchProps = {
    offColor: "#808080",
    onColor: "#7ea2ff",
    checkedIcon: false,
    uncheckedIcon: false,
    height: 12,
    width: 24
};

export const customSelectStyles = {

    control: (base, state) => ({
        ...base,
        borderColor: state.isFocused ? 'var(--primary)' : 'var(--border-color)',
        boxShadow: state.isFocused
            ? '0 0 0 0.1rem rgba(43, 65, 122, 0.25)' // still ok as is, optional to convert to a variable
            : 'none',
        '&:hover': {
            borderColor: 'var(--primary)',
        },
        minHeight: '40px',
        fontSize: '14px',
        backgroundColor: 'var(--dropdown-bg)',
        borderWidth: '1px',
        zIndex: 999,
        borderRadius: '5px'
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? 'var(--primary)'
            : state.isFocused
                ? 'var(--hover-primary)'
                : 'var(--color-white)',
        color: state.isSelected ? 'var(--text-color)' : 'var(--text-color)',
        '&:active': {
            backgroundColor: 'var(--primary)',
            color: 'var(--text-color)',
        },
        fontSize: 'var(--font-sm)',
    }),
    placeholder: (base) => ({
        ...base,
        color: 'var(--placeholder-color)',
        fontSize: 'var(--font-sm)',
    }),
    singleValue: (base) => ({
        ...base,
        color: 'var(--text-color)',
    }),
    menu: (base) => ({
        ...base,
        zIndex: 9999,
        backgroundColor: 'var(--dropdown-bg)',
        border: '1px solid var(--border-color)'
    }),
    menuList: (base) => ({
        ...base,
        maxHeight: '180px',   // limit height
        overflowY: 'auto',    // enable vertical scroll
        paddingRight: '5px',  // avoid scrollbar overlapping text
    }),
};


const formatNumberFixed = (num, digits = 2) => {
    num = Number(num);
    if (isNaN(num)) return "";

    return num % 1 !== 0
        ? num.toFixed(digits)
        : num.toString();
};
export const compactNumberWithTooltip = (amount, title = "", digits) => {
    // Check if amount is a valid number
    if (isNaN(amount) || amount === "" || amount === null) {
        return <span></span>;  // Return an empty span if amount is invalid
    }
    const tooltipText = title ? `${title}: ${amount}` : `${amount}`;

    return (
        <WithTooltip tooltipText={tooltipText}>
            <span >
                {formatNumberFixed(amount, digits)}
            </span>
        </WithTooltip>
    );
};
export function getTruncatedText(text, maxLength = 20, className = "", tag = "div") {
    const isTruncated = text?.length > maxLength;
    const truncatedText = isTruncated ? `${text.substring(0, maxLength)}...` : text;
    const CustomTag = tag;
    return (
        <WithTooltip tooltipText={text}>
            <CustomTag
                className={` ${className}`}
                style={{
                    whiteSpace: "wrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    cursor: "pointer",
                }}
            >
                {truncatedText}
            </CustomTag>
        </WithTooltip>
    );
}
export const getTransactionTypeBadge = (type) => {
    if (type === null || type === undefined) {
        return "-";
    }

    const label = type?.charAt(0)?.toUpperCase() + type?.slice(1)
    return (
        <span className={`badge ${type === TRANSACTION_TYPE.CREDIT ? 'success' : 'danger'}-badge p-2`} style={{ fontSize: 'var(--font-xs)' }}>
            {label}
        </span>
    );
};


export const getChildStatus = (status) => {
    if (!status) return null;

    let label = "";
    let colorClass = "";

    switch (status) {
        case CHILD_ACCOUNT_STATUS.ACTIVE:
            label = "Active";
            colorClass = "success-badge";  // green
            break;
        case CHILD_ACCOUNT_STATUS.EXPIRED:
            label = "Expired";
            colorClass = "danger-badge";   // red
            break;
        case CHILD_ACCOUNT_STATUS.SL_HIT:
            label = "SL Hit";
            colorClass = "danger-badge";   // red or custom color
            break;
        case CHILD_ACCOUNT_STATUS.TGT_HIT:
            label = "Target Hit";
            colorClass = "success-badge";  // green or custom color
            break;
        case CHILD_ACCOUNT_STATUS.STOPPED:
            label = "Stopped";
            colorClass = "danger-badge";  // green or custom color
            break;
        default:
            label = status;
            colorClass = "sky-badge"; // fallback
    }

    return (
        <small className={`badge  align-items-center fs-small gap-1 ${colorClass}`}>
            {label}
        </small>
    );
};

//This will return pnl with original value with color and mdi icon
export function getPnlComponent(pnlVal) {
    if (pnlVal == null || pnlVal === undefined) {
        return "";
    }

    pnlVal = Number(parseFloat(pnlVal)?.toFixed(2));
    let colorClass = '';
    let icon = null;

    // Determine the color class and icon based on the value
    if (pnlVal < 0) {
        colorClass = 'danger-text';
        icon = <i className="mdi mdi-arrow-down-bold fs-5"></i>;
    } else if (pnlVal === 0.0) {
        pnlVal = 0.0;
        icon = null; // No icon for zero
    } else if (pnlVal > 0) {
        colorClass = 'sucess-text';
        icon = <i className="mdi mdi-arrow-up-bold fs-5"></i>;
    }

    // Return the original pnlVal with color and icon
    return (
        <span className={`${colorClass} d-flex align-items-center justify-content-center gap-1`}>
            {pnlVal}
            {icon}
        </span>
    );
}

const getOrderBadgeClassName = (type) => [0, 2, 4, 6].includes(type) ? 'success' : 'danger';
export const getOrderTypeBadge = (type) => {
    if (type === null || type === undefined) {
        return "-";
    }

    const label = ORDER_EXECUTION_TYPE[type].charAt(0).toUpperCase() + ORDER_EXECUTION_TYPE[type].slice(1);

    return (
        <span className={`badge ${getOrderBadgeClassName(type)}-badge p-2`} style={{ fontSize: 'var(--font-xs)' }}>
            {label}
        </span>
    );
};


export const TOAST_OPTIONS = {
    // Shared styles
    style: {
        fontSize: '20px',
        borderRadius: "10px",
        padding: "12px 16px",
        color: "var(--text-color)",
    },
    // Success toast
    success: {
        style: { background: "var(--border-color)" },
        iconTheme: {
            primary: "var(--primary)",
            secondary: "var(--white-text)",
        },
    },
    // Error toast
    error: {
        style: { background: "var(--border-color)" },
        iconTheme: {
            primary: "var(--error)",
            secondary: "var(--white-text)",
        },
    },
};

export function getChargesBadge(data = {}, is_child = false) {
    const badges = [];

    // Helper for duration short form
    const formatDuration = (duration) => {
        if (!duration) return "-";
        return duration.toLowerCase() === "monthly" ? "mo" : "wk";
    };
    // FOR CHILD CHARGE  BADGE 
    if (is_child) {
        //  Child data uses charge_mode, charges, charge_duration
        const mode = getChargeMapping(data?.charge_mode);
        if (mode === STRATEGY_CHARGES_MODE.PREPAID) {
            badges.push(
                <WithTooltip key="prepaid" tooltipText={`Prepaid: $${data?.charges || "-"} / ${data?.charge_duration || "-"}`}    >
                    <span className="badge warning-badge d-flex align-items-center gap-1 cursor-pointer">
                        <i className="mdi mdi-calendar " />
                        ${data?.charges || "-"}/{formatDuration(data?.charge_duration)}
                    </span>
                </WithTooltip>
            );
        }
        if (mode === STRATEGY_CHARGES_MODE.PROFIT_SHARING) {
            badges.push(
                <WithTooltip key="profit" tooltipText={`Profit Sharing: ${data?.charges != null ? data?.charges + "%" : "-"} / ${data?.charge_duration || "-"}`} >
                    <span className="badge success-badge d-flex align-items-center gap-1 cursor-pointer">
                        <i className="mdi mdi-trending-up " />
                        {data?.charges != null ? `${data?.charges}%` : "-"}/{formatDuration(data?.charge_duration)}
                    </span>
                </WithTooltip>
            );
        }
        if (mode === STRATEGY_CHARGES_MODE.FREE) {
            badges.push(
                <WithTooltip key="free" tooltipText="Free - No charges">
                    <span className="badge info-badge d-flex align-items-center gap-1 cursor-pointer">
                        <i className="mdi mdi-gift " /> Free
                    </span>
                </WithTooltip>
            );
        }
    } else {
        //  Parent data uses is_free, is_pre_paid_charge, is_profit_sharing_charge
        if (data?.is_pre_paid_charge) {
            badges.push(
                <WithTooltip key="prepaid" tooltipText={`Prepaid: $${data?.pre_paid_charge || "-"} / ${data?.pre_paid_duration || "-"}`}     >
                    <span className="badge warning-badge  d-flex align-items-center gap-1 cursor-pointer">
                        <i className="mdi mdi-calendar " />
                        ${data?.pre_paid_charge || "-"}/{formatDuration(data?.pre_paid_duration)}
                    </span>
                </WithTooltip>
            );
        }
        if (data?.is_profit_sharing_charge) {
            badges.push(
                <WithTooltip key="profit" tooltipText={`Profit Sharing: ${data?.profit_sharing_percentage != null ? data?.profit_sharing_percentage + "%" : "-"} / ${data?.profit_sharing_duration || "-"}`}   >
                    <span className="badge success-badge d-flex align-items-center gap-1 cursor-pointer">
                        <i className="mdi mdi-trending-up " />
                        {data?.profit_sharing_percentage != null ? `${data?.profit_sharing_percentage}%` : "-"}/{formatDuration(data?.profit_sharing_duration)}
                    </span>
                </WithTooltip>
            );
        }
        if (data?.is_free) {
            badges.push(<WithTooltip key="free" tooltipText="Free - No charges">
                <span className="badge info-badge d-flex align-items-center gap-1 cursor-pointer">
                    <i className="mdi mdi-gift " /> Free
                </span>
            </WithTooltip>
            );
        }
    }
    if (!badges.length) return null;
    return (
        <div className="d-flex flex-wrap gap-2 mt-1">{badges}</div>
    );
}
export function getChargesText(data = {}, is_child = false) {
    const charges = [];

    const formatDuration = (duration) => {
        if (!duration) return "-";
        return duration.toLowerCase() === "monthly" ? "mo" : "wk";
    };

    if (is_child) {
        const mode = getChargeMapping(data?.charge_mode);

        if (mode === STRATEGY_CHARGES_MODE.PREPAID) {
            charges.push(`Prepaid: $${data?.charges || "-"} / ${formatDuration(data?.charge_duration)}`);
        }
        if (mode === STRATEGY_CHARGES_MODE.PROFIT_SHARING) {
            charges.push(`Profit Sharing: ${data?.charges != null ? data?.charges + "%" : "-"} / ${formatDuration(data?.charge_duration)}`);
        }
        if (mode === STRATEGY_CHARGES_MODE.FREE) {
            charges.push("Free - No charges");
        }
    } else {
        if (data?.is_pre_paid_charge) {
            charges.push(`Prepaid: $${data?.pre_paid_charge || "-"} / ${formatDuration(data?.pre_paid_duration)}`);
        }
        if (data?.is_profit_sharing_charge) {
            charges.push(`Profit Sharing: ${data?.profit_sharing_percentage != null ? data?.profit_sharing_percentage + "%" : "-"} / ${formatDuration(data?.profit_sharing_duration)}`);
        }
        if (data?.is_free) {
            charges.push("Free - No charges");
        }
    }

    return charges.length ? charges.join(" | ") : "-";
}

export const chartTooltipStyles = {
    contentStyle: {
        fontSize: "10px",
        borderRadius: "5px",
        backgroundColor: "var(--sub-card-bg)",
        color: "var(--text-color)",
        border: "1px solid var(--border-color)",
    },
    labelStyle: { color: "var(--text-color)" },
    itemStyle: { color: "var(--text-color)" },
};

export const axisTickStyle = {
    fontSize: "10px",
    color: 'var(--border-color)',
    fontWeight: 'bold'
}

export const getTop10MasterChartBarColor = (selected, item) => {
    if (selected?.value === TOP_10_MASTER_FILTER.TOTAL_AUM) return 'var(--sky)';
    if (selected?.value === TOP_10_MASTER_FILTER.NO_OF_COPIERS) return 'var(--warning)';

    if (
        selected?.value === TOP_10_MASTER_FILTER.ROI_PERCENTAGE ||
        selected?.value === TOP_10_MASTER_FILTER.WEEKLY_PROFIT_PERCENTAGE ||
        selected?.value === TOP_10_MASTER_FILTER.MONTHLY_PROFIT_PERCENTAGE ||
        selected?.value === TOP_10_MASTER_FILTER.THREE_MONTH_PROFIT_PERCENTAGE ||
        selected?.value === TOP_10_MASTER_FILTER.SIX_MONTH_PROFIT_PERCENTAGE ||
        selected?.value === TOP_10_MASTER_FILTER.YEARLY_PROFIT_PERCENTAGE
    ) {
        return item?.roi_percentage < 0 ? 'var(--error)' : 'var(--success)';
    }

    return 'var(--sky)';
};
